import React, { useState, useEffect } from "react";
import { Box, TextField, Button } from "@mui/material";
import { useGetDocsByQuery } from "../../../components/Firebase/useFirebaseQueries";
import DataGrid from "../../../components/DataGrid/DataGrid";
import useLocalStorage from "../../../Hooks/UseLocalstorage";
import { getTodayStartEndTimestamps } from "../../../utils/Utils";
import Notification from "../../../components/Notification/Notifications";
import ReturnBillTable from "./ReturnBillsTable";

const ReturnBillColumns = [
  { field: "returnBillNumber", headerName: "Return Bill Number", width: 150 },
  { field: "returnDate", headerName: "Date", width: 120 },
  { field: "patientName", headerName: "Patient Name", width: 200 },
  { field: "totalAmount", headerName: "Total Amount", width: 120 },
  {
    field: "actions",
    headerName: "Actions",
    width: 100,
    renderCell: (params) => (
      <Button
        variant="contained"
        color="primary"
        size="small"
        onClick={() => window.print()}
      >
        Print
      </Button>
    ),
  },
];

function ReturnBills() {
  const { startTimestamp, endTimestamp } = getTodayStartEndTimestamps();
  const [pharmacyId] = useLocalStorage("pharmacyId");
  const [rows, setRows] = useState([]);
  const [fromDate, setFromDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
  const [dateFilter, setDateFilter] = useState({
    from: startTimestamp,
    to: endTimestamp,
  });
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMsg, setNotificationMsg] = useState({
    severity: "",
    message: "",
  });

  const validateDates = (from, to) => {
    const today = new Date().toISOString().split("T")[0];
    let isValid = true;

    if (from > today) {
      setNotificationMsg({
        severity: "error",
        message: "From date cannot be in the future.",
      });
      isValid = false;
    } else if (to > today) {
      setNotificationMsg({
        severity: "error",
        message: "To date cannot be in the future.",
      });
      isValid = false;
    } else if (to < from) {
      setNotificationMsg({
        severity: "error",
        message: "To date cannot be earlier than From date.",
      });
      isValid = false;
    }

    setShowNotification(!isValid);
    return isValid;
  };

  const handleSearch = () => {
    const dateObjFrom = new Date(fromDate);
    dateObjFrom.setHours(0, 0, 0, 0);
    const dateObjTo = new Date(toDate);
    dateObjTo.setHours(23, 59, 59, 999);

    if (validateDates(fromDate, toDate)) {
      setDateFilter({
        from: dateObjFrom.getTime(),
        to: dateObjTo.getTime(),
      });
    }
  };

  const { data: returnBills, isLoading: returnBillsLoading } =
    useGetDocsByQuery(
      "pharmacyReturnBills",
      [
        { field: "pharmacyId", operator: "==", value: pharmacyId },
        { field: "createAt", operator: ">=", value: dateFilter.from },
        { field: "createAt", operator: "<=", value: dateFilter.to },
      ],
      true,
      [pharmacyId, dateFilter.from, dateFilter.to]
    );

  useEffect(() => {
    if (returnBills) {
      const filteredReturnBills = returnBills.map((bill) => {
        const medicines = bill.medicinesReturned || [];
        const totalAmount = medicines.reduce((acc, med) => {
          return acc + parseFloat(med.returnQuantity * med.price || 0);
        }, 0);
        return {
          id: bill.id,
          returnBillNumber: bill.billNumber || bill.id,
          returnDate: new Date(bill.createAt).toISOString().split("T")[0],
          patientName: bill.patientName || "N/A",
          totalAmount: totalAmount.toFixed(2),
        };
      });

      setRows(filteredReturnBills);

      if (filteredReturnBills.length === 0) {
        setNotificationMsg({
          severity: "info",
          message: "No return bills found for selected dates.",
        });
        setShowNotification(true);
      }
    }
  }, [returnBills]);

  return (
    <Box sx={{ padding: 2 }}>
      <Box
        sx={{
          background: "#FFFFFFFF",
          borderRadius: "6px",
          borderWidth: "1px",
          borderColor: "#F3F4F6FF",
          borderStyle: "solid",
          boxShadow: "0px 0px 2px #171a1f",
          padding: "10px",
          display: "flex",
          alignItems: "center",
          gap: 2,
          marginBottom: 2,
        }}
      >
        <TextField
          label="From Date"
          type="date"
          size="small"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          sx={{ width: 200, backgroundColor: "#EAECF0FF" }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: new Date().toISOString().split("T")[0] }}
        />
        <TextField
          label="To Date"
          type="date"
          size="small"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          sx={{ width: 200, backgroundColor: "#EAECF0FF" }}
          InputLabelProps={{ shrink: true }}
          inputProps={{ max: new Date().toISOString().split("T")[0] }}
        />
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleSearch}
        >
          Search
        </Button>
      </Box>
      <Box sx={{ marginTop: 2 }}>
        <ReturnBillTable rows={rows} />
      </Box>
      <Notification
        message={notificationMsg.message}
        action={showNotification}
        onClose={() => setShowNotification(false)}
        severity={notificationMsg.severity}
      />
    </Box>
  );
}

export default ReturnBills;
